import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { gov, downloadFile } from "@/lib/governanceApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { card, label, input, Pill, SectionTitle, Act, errMsg } from "./ui";

const ApproveDialog = ({ doc, open, onClose, onDone }) => {
  const [f, setF] = useState({ approver_name: "", legal_reviewed: false, legal_reviewer: "", note: "" });
  const go = async () => {
    try { await gov.docTransition(doc.id, { action: "approve", ...f }); toast.success("Approved and frozen"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="approve-dialog">
        <DialogHeader><DialogTitle>Approve “{doc.title}” v{doc.version}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">Approval freezes the exact text with a SHA-256 hash. Approval is not publication: disclosing to customers is a separate step.</p>
        <label className={label}>Approver (authorised person)</label>
        <input data-testid="approver-name" className={input} value={f.approver_name} onChange={(e) => setF({ ...f, approver_name: e.target.value })} />
        {doc.legal_review_required && (
          <div className="border-2 border-violet-200 bg-violet-50 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="legal-reviewed" checked={f.legal_reviewed} onChange={(e) => setF({ ...f, legal_reviewed: e.target.checked })} /> Reviewed by qualified UK legal counsel</label>
            <input data-testid="legal-reviewer" placeholder="Reviewer name / firm" className={input} value={f.legal_reviewer} onChange={(e) => setF({ ...f, legal_reviewer: e.target.value })} />
          </div>
        )}
        <Act onClick={go} data-testid="approve-confirm">Approve</Act>
      </DialogContent>
    </Dialog>
  );
};

const Viewer = ({ id, onChanged }) => {
  const qc = useQueryClient();
  const { data: d } = useQuery({ queryKey: ["gov-doc", id], queryFn: () => gov.document(id) });
  const [text, setText] = useState(null);
  const [approve, setApprove] = useState(false);
  if (!d) return <div className="font-mono text-sm text-zinc-400 p-4">Loading…</div>;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["gov-doc", id] }); onChanged(); };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); setText(null); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const t = (action) => run(() => gov.docTransition(id, { action }), `Done: ${action}`);
  return (
    <div className={`${card} p-4 space-y-3`} data-testid="doc-viewer">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-lg font-extrabold mr-2">{d.title} · v{d.version}</h3>
        <Pill value={d.status} testid="doc-status" />
        {d.disclosed_to_customers && <Pill value="disclosed to customer" />}
        {d.integrity_ok === false && <Pill value="critical" testid="doc-integrity-fail" />}
      </div>
      <div className="text-xs text-zinc-500 grid md:grid-cols-2 gap-1">
        <div><strong>Purpose:</strong> {d.purpose}</div><div><strong>Applicability:</strong> {d.applicability}</div>
        <div><strong>Owner:</strong> {d.owner}</div><div><strong>Acceptance:</strong> {d.acceptance}</div>
        <div><strong>Legal review required:</strong> {d.legal_review_required ? "Yes" : "No"}</div>
        <div><strong>Approved:</strong> {d.approved_at ? `${d.approved_at.slice(0, 10)} by ${d.approved_by}${d.legal_reviewer ? ` · legal: ${d.legal_reviewer}` : ""}` : "—"}</div>
        <div><strong>Effective / review:</strong> {d.effective_date || "—"} / {d.review_date || "—"}</div>
        {d.content_hash && <div className="font-mono break-all"><strong>SHA-256:</strong> {d.content_hash}</div>}
      </div>
      <div className="flex flex-wrap gap-2">
        {d.status === "draft" && text === null && <Act tone="ghost" onClick={() => setText(d.content_md)} data-testid="doc-edit">Edit draft</Act>}
        {d.status === "draft" && <Act onClick={() => t("submit")} data-testid="doc-submit">Submit for review</Act>}
        {d.status === "in_review" && <><Act onClick={() => setApprove(true)} data-testid="doc-approve">Approve…</Act><Act tone="ghost" onClick={() => t("return")} data-testid="doc-return">Return to draft</Act></>}
        {d.status === "approved" && <>
          <Act tone="ghost" onClick={() => t(d.disclosed_to_customers ? "undisclose" : "disclose")} data-testid="doc-disclose">{d.disclosed_to_customers ? "Stop disclosing" : "Disclose to customers"}</Act>
          <Act tone="danger" onClick={() => t("withdraw")} data-testid="doc-withdraw">Withdraw</Act>
        </>}
        {["approved", "superseded", "withdrawn"].includes(d.status) && <Act tone="ghost" onClick={() => run(() => gov.newVersion(id), "New draft version created")} data-testid="doc-new-version">New version</Act>}
        <Act tone="ghost" onClick={() => downloadFile(`/documents/${id}/pdf`, `${d.key}-v${d.version}.pdf`)} data-testid="doc-pdf"><FileDown size={14} className="mr-1" /> PDF</Act>
      </div>
      {text !== null ? (
        <div className="space-y-2">
          <textarea data-testid="doc-editor" className={`${input} font-mono text-xs`} rows={22} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex gap-2">
            <Act onClick={() => run(() => gov.saveDocument(id, { content_md: text }), "Draft saved")} data-testid="doc-save">Save draft</Act>
            <Act tone="ghost" onClick={() => setText(null)}>Cancel</Act>
          </div>
        </div>
      ) : (
        <pre data-testid="doc-content" className="whitespace-pre-wrap text-xs leading-relaxed bg-zinc-50 border border-zinc-200 p-4 max-h-[60vh] overflow-auto font-mono">{d.rendered_md}</pre>
      )}
      <ApproveDialog doc={d} open={approve} onClose={() => setApprove(false)} onDone={refresh} />
    </div>
  );
};

export default function Documents() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["gov-docs"], queryFn: gov.documents });
  const [sel, setSel] = useState(null);
  const groups = data.reduce((a, d) => ({ ...a, [d.key]: [...(a[d.key] || []), d] }), {});
  return (
    <div>
      <SectionTitle sub="Product-specific drafts. Nothing here is published or binding until approved and, where marked, legally reviewed. Approved versions are frozen and never regenerated.">Policy & document library</SectionTitle>
      <div className="grid lg:grid-cols-[340px_1fr] gap-4">
        <div className={`${card} divide-y divide-zinc-100`} data-testid="doc-list">
          {Object.entries(groups).map(([key, vs]) => (
            <div key={key} className="p-3">
              <div className="font-bold text-sm mb-1">{vs[0].title}</div>
              {vs.map((v) => (
                <button key={v.id} data-testid={`doc-${key}-v${v.version}`} onClick={() => setSel(v.id)} className={`w-full flex justify-between items-center text-xs px-2 py-1 hover:bg-zinc-50 ${sel === v.id ? "bg-sky-50" : ""}`}>
                  <span className="font-mono">v{v.version}</span><Pill value={v.status} />
                </button>
              ))}
            </div>
          ))}
        </div>
        {sel ? <Viewer key={sel} id={sel} onChanged={() => qc.invalidateQueries({ queryKey: ["gov-docs"] })} /> : <div className="text-sm text-zinc-400 p-6">Select a document version.</div>}
      </div>
    </div>
  );
}
